// Modo Sandbox: esta persona ve el tenant único de ejemplo, en solo lectura.
//
// El estado es del USUARIO (`users/{id}.sandboxView`), no del navegador ni de la
// compañía: lo dice la API (`GET /sandbox`) y se cambia con `PUT /sandbox`, que es
// una sola escritura. Lo leen la leyenda superior, el menú de cuenta y Perfil →
// Ajustes; por eso vive en un store y no en cada pantalla.
//
// Falla cerrado hacia «apagado»: si la API no responde no se pinta la leyenda ni
// el interruptor. Es un modo de demostración, no algo de lo que dependa operar.

import { create } from "zustand";

import { enterSandboxView, exitSandboxView, isSandboxView } from "./sandboxView";

// El cliente de la API se carga al usarlo: este store lo importa el menú de
// cuenta, y traerlo al cargar el módulo arrastraría Firebase a cada pantalla
// (y a cada prueba) que pinte el menú.
const client = async () => (await import("../../utils/api")).default;

const apiUrl = import.meta.env.VITE_FIREBASE_API_URL;

export interface SandboxStatus {
  enabled: boolean;
  /** ¿Esta persona puede entrar o salir? (sesión de persona y tenant ya construido). */
  canToggle: boolean;
  /** ¿Puede vincular equipos a su compañía real? (quien la administra). */
  canLink: boolean;
  /** Id de la compañía de ejemplo. `null` si la API no lo manda. */
  tenantId: string | null;
  changedAt: string | null;
  counts: Record<string, number>;
  /** Registros de ejemplo por dominio: `{ fleet: 50, pos: 46, … }`. */
  domains: Record<string, number>;
  /** Total de registros de ejemplo (0 si la API es anterior). */
  total: number;
  /**
   * Unidades y equipos REALES de la compañía de esta persona (CC-CONSOLE#1523).
   * `null` si la API no lo manda: la leyenda no afirma nada.
   */
  realCounts: RealCounts | null;
}

export interface RealCounts {
  vehicles: number;
  devices: number;
}

/** Unidades (vehículos) de ejemplo: la cifra de `counts.vehicles`. */
export function sampleUnits(counts: Record<string, number>): number {
  return Number(counts.vehicles) || 0;
}

/** Cuántos módulos (dominios) tienen datos de ejemplo. */
export function sampleDomains(domains: Record<string, number>): number {
  return Object.values(domains).filter((n) => Number(n) > 0).length;
}

interface SandboxState extends SandboxStatus {
  /** Sesión + compañía elegida para la que se cargó (`sandboxSessionKey`; otra ⇒ se vuelve a pedir). */
  loadedFor: string | null;
  /**
   * Token de la sesión para la que la API YA contestó. `loadedFor` se marca
   * antes de pedir (para no pedir dos veces); mientras no coincida con este,
   * `enabled` todavía es el valor por omisión, no el estado real.
   */
  resolvedFor: string | null;
  busy: boolean;
  error: boolean;
  load: (opts?: { force?: boolean }) => Promise<void>;
  /** Entra o sale. Devuelve si el cambio se aplicó. */
  setEnabled: (enabled: boolean) => Promise<boolean>;
  reset: () => void;
}

const OFF: SandboxStatus = { enabled: false, canToggle: false, canLink: false, tenantId: null, changedAt: null, counts: {}, domains: {}, total: 0, realCounts: null };

const readCounts = (raw: unknown): Record<string, number> => (raw && typeof raw === "object" ? (raw as Record<string, number>) : {});

function readRealCounts(raw: unknown): RealCounts | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : 0);
  return { vehicles: n(r.vehicles), devices: n(r.devices) };
}

/** `{ meta, data: <estado> }`; tolera un sobre de más o de menos. */
export function readSandboxStatus(raw: unknown): SandboxStatus {
  let body = raw as Record<string, unknown> | null | undefined;
  for (let i = 0; i < 2 && body && typeof body.enabled !== "boolean" && body.data && typeof body.data === "object"; i += 1) {
    body = body.data as Record<string, unknown>;
  }
  if (!body || typeof body.enabled !== "boolean") return OFF;
  return {
    enabled: body.enabled,
    canToggle: body.canToggle === true,
    canLink: body.canLink === true,
    tenantId: typeof body.tenantId === "string" && body.tenantId ? body.tenantId : null,
    changedAt: typeof body.changedAt === "string" ? body.changedAt : null,
    counts: readCounts(body.counts),
    domains: readCounts(body.domains),
    total: typeof body.total === "number" && Number.isFinite(body.total) && body.total > 0 ? body.total : 0,
    realCounts: readRealCounts(body.realCounts),
  };
}

/**
 * Pone este navegador en el estado que dice la API. Devuelve si hubo que cambiar
 * algo: las listas de la consola viven en muchos stores y cachés distintos, y
 * recargar es la única forma de que TODAS muestren los datos correctos.
 */
export function syncSandboxView(status: Pick<SandboxStatus, "enabled" | "tenantId">): boolean {
  if (status.enabled === isSandboxView()) return false;
  if (status.enabled) {
    if (!status.tenantId) return false;
    enterSandboxView(status.tenantId);
  } else {
    exitSandboxView();
  }
  return true;
}

/** Clave de la consulta: la sesión (token) y la compañía elegida en el encabezado. */
export function sandboxSessionKey(): string {
  const token = localStorage.getItem("token") || "";
  if (!token) return "";
  return `${token}|${localStorage.getItem("companyId") || ""}`;
}

export const useSandboxStore = create<SandboxState>((set, get) => ({
  ...OFF,
  loadedFor: null,
  resolvedFor: null,
  busy: false,
  error: false,

  load: async ({ force = false } = {}) => {
    const token = sandboxSessionKey();
    if (!token) {
      if (get().loadedFor !== null) set({ ...OFF, loadedFor: null, resolvedFor: null, error: false });
      return;
    }
    if (!force && get().loadedFor === token) return;
    // Se marca antes de pedir: dos pantallas que montan a la vez piden una sola vez.
    set({ loadedFor: token });
    try {
      const res = await (await client()).get(`${apiUrl}/sandbox`);
      if (get().loadedFor !== token) return;
      const status = readSandboxStatus(res.data);
      set({ ...status, error: false, resolvedFor: token });
      // Otro dispositivo cambió la vista, o el alta acaba de encenderla.
      if (syncSandboxView(status)) window.location.reload();
    } catch {
      if (get().loadedFor === token) set({ ...OFF });
    }
  },

  setEnabled: async (enabled) => {
    if (get().busy) return false;
    set({ busy: true, error: false });
    try {
      const res = await (await client()).put(`${apiUrl}/sandbox`, { enabled });
      const status = readSandboxStatus(res.data);
      syncSandboxView(status);
      const token = sandboxSessionKey() || null;
      set({ ...status, busy: false, loadedFor: token, resolvedFor: token });
      return true;
    } catch {
      set({ busy: false, error: true });
      return false;
    }
  },

  reset: () => set({ ...OFF, loadedFor: null, resolvedFor: null, busy: false, error: false }),
}));

/** Cambia el modo y, si se aplicó, recarga la página (ver `syncSandboxView`). */
export async function toggleSandbox(enabled: boolean, reload: () => void = () => window.location.reload()): Promise<boolean> {
  const ok = await useSandboxStore.getState().setEnabled(enabled);
  if (ok) reload();
  return ok;
}
